import { QueryClientProvider } from '@tanstack/react-query';
import { lazy, useState } from 'react';
import { createBrowserRouter, RouterProvider, type RouteObject } from 'react-router';
import { Toaster } from 'sonner';
import { ErrorBoundary } from '@/components/common';
import { PublicShell } from '@/components/shell';
import adminRoutes from '@/features/admin/routes';
import clientRoutes from '@/features/client/routes';
import employeeRoutes from '@/features/employee/routes';
import { AuthProvider } from '@/lib/auth/AuthProvider';
import { createQueryClient } from '@/lib/query';
import { HomeRedirect, RequireRole, RequireStaff } from './guards';

const HomePage = lazy(() => import('@/features/public/HomePage'));
const LoginPage = lazy(() => import('@/features/public/LoginPage'));
const RegisterPage = lazy(() => import('@/features/public/RegisterPage'));
const MockCheckoutPage = lazy(() => import('@/features/public/MockCheckoutPage'));
const NotFoundPage = lazy(() => import('@/features/public/NotFoundPage'));
const ForgotPasswordPage = lazy(() => import('@/features/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/features/auth/ResetPasswordPage'));
const ServicesPage = lazy(() => import('@/features/public/ServicesPage'));
const FaqPage = lazy(() => import('@/features/public/FaqPage'));
const SectorsPage = lazy(() => import('@/features/public/SectorsPage'));
const AboutPage = lazy(() => import('@/features/public/AboutPage'));
const QuotePage = lazy(() => import('@/features/public/QuotePage'));
const LegalPage = lazy(() => import('@/features/public/LegalPage'));

const routes: RouteObject[] = [
  {
    element: <PublicShell />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'services', element: <ServicesPage /> },
      { path: 'soumission', element: <QuotePage /> },
      { path: 'faq', element: <FaqPage /> },
      { path: 'secteurs', element: <SectorsPage /> },
      { path: 'a-propos', element: <AboutPage /> },
      { path: 'confidentialite', element: <LegalPage kind="privacy" /> },
      { path: 'conditions', element: <LegalPage kind="terms" /> },
      { path: 'annulation', element: <LegalPage kind="cancellation" /> },
      { path: 'connexion', element: <LoginPage /> },
      { path: 'inscription', element: <RegisterPage /> },
      { path: 'mot-de-passe-oublie', element: <ForgotPasswordPage /> },
      { path: 'reinitialiser-mot-de-passe', element: <ResetPasswordPage /> },
      {
        path: 'paiement-simule',
        element: (
          <RequireRole roles={['CLIENT', 'OWNER', 'ADMIN']}>
            <MockCheckoutPage />
          </RequireRole>
        ),
      },
    ],
  },
  { path: 'app', element: <HomeRedirect /> },
  { path: 'client', element: <RequireRole roles={['CLIENT']}>{clientRoutes.element}</RequireRole>, children: clientRoutes.children },
  { path: 'employe', element: <RequireRole roles={['EMPLOYEE']}>{employeeRoutes.element}</RequireRole>, children: employeeRoutes.children },
  { path: 'admin', element: <RequireStaff>{adminRoutes.element}</RequireStaff>, children: adminRoutes.children },
  { path: '*', element: <PublicShell><NotFoundPage /></PublicShell> },
];

const router = createBrowserRouter(routes);

export default function App() {
  const [queryClient] = useState(createQueryClient);
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <RouterProvider router={router} />
          <Toaster position="top-center" richColors closeButton />
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
